import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    TextField,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    Tooltip,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebounce } from "hooks/useDebounced";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";

function AllSubadminList() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [subadmins, setSubadmins] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);

    const debouncedSearch = useDebounce(search, 1000);

    const fetchSubadmins = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: page + 1,
                limit: rowsPerPage,
                search,
            });

            const res = await fetch(`${process.env.REACT_APP_API_URL}admin/sub-admins?${params}`, {
                headers: {
                    authkey: process.env.REACT_APP_AUTH_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            const result = await res.json();

            if (result.code === 200) {
                setSubadmins(result.data.subAdmins || []);
                setTotal(result.data.pagination?.total || 0);
            } else {
                toast.error(result.message || t("addSubadminList.errors.fetchFailed"));
            }
        } catch (err) {
            console.error(err);
            toast.error(t("addSubadminList.errors.somethingWentWrong"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubadmins();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, debouncedSearch]);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDeactivateSubadmin = async (id) => {
        const confirm = await swal({
            title: t("addSubadminList.confirm.title"),
            text: t("addSubadminList.confirm.text"),
            icon: "warning",
            buttons: [
                t("addSubadminList.confirm.cancel"),
                t("addSubadminList.confirm.confirm"),
            ],
            dangerMode: true,
        });

        if (!confirm) return;

        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}admin/deactivate-sub-admin/${id}`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        authkey: process.env.REACT_APP_AUTH_KEY,
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );

            const result = await res.json();
            if (result.code === 200) {
                toast.success(result.message || t("addSubadminList.messages.deactivated"));
                fetchSubadmins();
            } else {
                toast.error(result.message || t("addSubadminList.messages.failed"));
            }
        } catch (error) {
            console.error(error);
            toast.error(t("addSubadminList.messages.error"));
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                {t("addSubadminList.title")} ({total} {t("addSubadminList.total")})
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        fullWidth
                        label={t("addSubadminList.searchLabel")}
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("addSubadminList.searchPlaceholder")}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate("/add-subadmin")}
                    >
                        {t("addSubadminList.addButton")}
                    </Button>
                </Grid>
            </Grid>

            <Paper>
                <TableContainer sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("addSubadminList.columns.sno")}</TableCell>
                                <TableCell>{t("addSubadminList.columns.name")}</TableCell>
                                <TableCell>{t("addSubadminList.columns.email")}</TableCell>
                                <TableCell>{t("addSubadminList.columns.status")}</TableCell>
                                <TableCell>{t("addSubadminList.columns.action")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        {t("addSubadminList.loading")}
                                    </TableCell>
                                </TableRow>
                            ) : subadmins.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        {t("addSubadminList.noData")}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                subadmins.map((subadmin, index) => (
                                    <TableRow hover key={subadmin._id}>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell>{subadmin.name}</TableCell>
                                        <TableCell>{subadmin.email}</TableCell>
                                        <TableCell>
                                            {subadmin.isActive ? (
                                                <Typography color="green">
                                                    {t("addSubadminList.status.active")}
                                                </Typography>
                                            ) : (
                                                <Typography color="error">
                                                    {t("addSubadminList.status.deactivated")}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {subadmin.isActive ? (
                                                <Tooltip title={t("addSubadminList.tooltips.deactivate")}>
                                                    <Button
                                                        color="error"
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handleDeactivateSubadmin(subadmin._id)}
                                                        startIcon={<DeleteIcon />}
                                                    >
                                                        {t("addSubadminList.actions.deactivate")}
                                                    </Button>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="body2" color="textSecondary">
                                                    {t("addSubadminList.actions.notAvailable")}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
}

export default AllSubadminList;

